/**
 * Leads API
 *
 * API functions for CRM lead pipeline management
 */

import axios from "./axiosInstance";
import config from "./config";
import { leadDisplayName } from "../utils/personName";

const BASE_URL = config.BASE_URL;

// ============================================
// TYPES
// ============================================

export type LeadStage =
  | "new"
  | "contacted"
  | "qualified"
  | "trial_scheduled"
  | "trial_completed"
  | "won"
  | "lost";

export type LeadSource =
  | "facebook_ads"
  | "google_ads"
  | "linkedin"
  | "website_form"
  | "phone_call"
  | "email"
  | "referral"
  | "walk_in"
  | "instagram"
  | "tiktok"
  | "other"
  | "registration_form"
  | "contact_form"
  | "ai_receptionist"
  | "ai_chat"
  | "trial_signup";

export interface LeadAthleteProgramSlot {
  id?: string;
  start_time?: string;
  end_time?: string;
  day_of_week?: string;
  [key: string]: unknown;
}

export interface LeadAthleteProgram {
  slot?: LeadAthleteProgramSlot;
  program_id?: string;
  [key: string]: unknown;
}

export interface LeadAthlete {
  first_name?: string;
  last_name?: string;
  firstName?: string;
  lastName?: string;
  dob?: string;
  dateOfBirth?: string;
  gender?: string;
  programs?: LeadAthleteProgram[];
  [key: string]: unknown;
}

export interface LeadAddress {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  [key: string]: unknown;
}

export interface LeadEmergencyContact {
  name?: string;
  first_name?: string;
  last_name?: string;
  relationship?: string;
  contact_type?: string;
  phone?: string;
  mobile_phone?: string;
  home_phone?: string;
  work_phone?: string;
  email?: string;
  [key: string]: unknown;
}

export interface LeadMetadata {
  athletes?: LeadAthlete[];
  address?: LeadAddress;
  emergency_contacts?: LeadEmergencyContact[];
  [key: string]: unknown;
}

export interface LeadSourceUtm {
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
  [key: string]: unknown;
}

export interface LeadSourceDetails {
  platform?: string;
  form_name?: string;
  page_url?: string;
  referrer?: string;
  utm?: LeadSourceUtm;
  campaign_id?: string;
  ad_id?: string;
  leadgen_id?: string;
  call_sid?: string;
  additional_sources?: unknown[];
  device_type?: string;
  browser?: string;
  os?: string;
  screen?: string;
  viewport?: string;
  language?: string;
  timezone?: string;
  url?: string;
  [key: string]: unknown;
}

export interface Lead {
  id: string;
  tenant_id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  source: LeadSource;
  source_details: LeadSourceDetails;
  stage: LeadStage;
  stage_changed_at: string;
  assigned_to: string | null;
  assigned_user?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  } | null;
  converted_to_membership_id: string | null;
  converted_membership?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  } | null;
  converted_at: string | null;
  lost_reason: string | null;
  lost_at: string | null;
  notes: string | null;
  interests: string[];
  preferred_contact_method: string;
  metadata: LeadMetadata;
  // Athlete info (the person attending the trial class)
  athlete_first_name: string | null;
  athlete_last_name: string | null;
  athlete_dob: string | null;
  athlete_gender: string | null;
  // Trial class assignment
  trial_program_id: string | null;
  trial_slot_id: string | null;
  trial_date: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  // Soft-archive timestamp (Daniel 2026-06-04). NULL = visible.
  archived_at?: string | null;
  // The last time staff contacted this lead (email, text, logged call), who
  // did, and how. Comes with the lead list; no extra request per card.
  last_contacted_at?: string | null;
  last_contacted_by?: string | null;
  last_contacted_via?: LeadContactChannel | null;
  last_contacted_by_user?: { id: string; first_name: string | null; last_name: string | null } | null;
  // The last time the lead got back in touch, and how. Written by the server
  // (services/leadReply) for a text, a call, the contact form, a new inquiry
  // with the same email, or staff logging "they replied".
  replied_at?: string | null;
  replied_via?: string | null;
  // Set when that reply brought the lead back from the archive.
  unarchived_by_reply_at?: string | null;
}

/** What the server did when a reply was recorded. */
export interface LeadReplyResult {
  isLead: boolean;
  /** The lead was archived and is now back in the pipeline. */
  revived: boolean;
  stamped: boolean;
  noted: boolean;
  notified: boolean;
  stage: string | null;
  at: string;
  channel: string;
}

/** "by text", "by email", ... for the pipeline card. "" when there is nothing to add. */
export const getReplyHow = (via?: string | null): string => {
  switch (via) {
    case "email":
      return "by email";
    case "text":
      return "by text";
    case "phone":
      return "by phone";
    case "in_person":
      return "in person";
    case "contact_form":
      return "through the contact form";
    case "ai_chat":
      return "through the website chat";
    case "registration_form":
      return "by starting to sign up";
    case "trial_signup":
      return "by signing up for a trial";
    default:
      return "";
  }
};

/**
 * Has the lead replied since staff last contacted them? This is what the
 * "Replied" marker on the card means: it goes away once staff contact the
 * lead again, with nothing to clear by hand.
 */
export const leadHasUnansweredReply = (
  lead: Pick<Lead, "replied_at" | "last_contacted_at">
): boolean => {
  if (!lead.replied_at) return false;
  const replied = new Date(lead.replied_at).getTime();
  if (Number.isNaN(replied)) return false;
  if (!lead.last_contacted_at) return true;
  const contacted = new Date(lead.last_contacted_at).getTime();
  return Number.isNaN(contacted) || replied > contacted;
};

/**
 * Apply a recorded reply to the lead held in the page, so the card and the
 * side panel show it (and an archived lead reappears) without a reload.
 */
export const applyLeadReply = <T extends Lead>(
  lead: T,
  reply: LeadReplyResult | null | undefined
): T => {
  if (!reply || !reply.stamped) return lead;
  const next: T = { ...lead, replied_at: reply.at, replied_via: reply.channel };
  if (reply.revived) {
    next.archived_at = null;
    next.unarchived_by_reply_at = reply.at;
    if (reply.stage && reply.stage !== lead.stage) {
      next.stage = reply.stage as T["stage"];
      next.stage_changed_at = reply.at;
    }
  }
  return next;
};

/** What converting a lead will bill and create (GET /leads/:id/convert-preview). */
export interface LeadConvertPreview {
  registration_fee: {
    will_bill: boolean;
    amount: number;
    configured_amount: number;
    /** 'conversion' | 'no_fee_configured' | 'fee_already_exists' */
    reason: string;
  };
  athletes: { name: string; classes: number }[];
  classes_total: number;
  will_enroll: boolean;
  existing_invoice_count: number;
  has_email: boolean;
  /**
   * A card payment taken at online sign-up, as the lead records it. Converting
   * checks it with Stripe and puts it on the account (2026-10-06: a family
   * charged $94 at sign-up was billed the $94 again when converted).
   */
  signup_payment?: LeadSignupPayment | null;
}

/** A card payment the family made at online sign-up. */
export interface LeadSignupPayment {
  payment_intent_id: string;
  /** Dollars, or null when the lead does not record the amount. */
  amount: number | null;
  /** Already on an account: converting leaves it as it is. */
  already_recorded?: boolean;
}

/** What a conversion billed (returned by POST /leads/:id/convert). */
export interface LeadConvertBilling {
  billed_now: boolean;
  registration_fee: { billed: boolean; amount: number; invoice_id: string | null; reason: string | null };
  /** The sign-up card payment, when the lead had one. */
  signup_payment?: {
    applied: boolean;
    amount: number | null;
    payment_intent_id: string | null;
    /** null | 'recorded' | 'refunded' | 'not_paid' */
    reason: string | null;
  };
}

export type LeadContactChannel = "email" | "call" | "text" | "in_person" | "note";

/**
 * What the server did when a contact was recorded: `moved` is true when the
 * lead went from New to Contacted because of it.
 */
export interface LeadContactResult {
  moved: boolean;
  stamped: boolean;
  at: string;
  channel: LeadContactChannel;
}

/** "Emailed", "Called", ... for the pipeline card. */
export const getContactVerb = (channel?: LeadContactChannel | null): string => {
  switch (channel) {
    case "email":
      return "Emailed";
    case "call":
      return "Called";
    case "text":
      return "Texted";
    case "in_person":
      return "Met";
    default:
      return "Contacted";
  }
};

/**
 * Apply a recorded contact to the lead held in the page, so the card and the
 * side panel show it without a reload.
 */
export const applyLeadContact = <T extends Lead>(
  lead: T,
  contact: LeadContactResult | null | undefined,
  by?: { id: string; first_name?: string | null; last_name?: string | null } | null
): T => {
  if (!contact || (!contact.moved && !contact.stamped)) return lead;
  const next: T = { ...lead };
  if (contact.moved) {
    next.stage = "contacted";
    next.stage_changed_at = contact.at;
  }
  if (contact.stamped) {
    next.last_contacted_at = contact.at;
    next.last_contacted_via = contact.channel;
    next.last_contacted_by = by?.id || null;
    next.last_contacted_by_user = by
      ? { id: by.id, first_name: by.first_name || null, last_name: by.last_name || null }
      : null;
  }
  return next;
};

export interface LeadActivity {
  id: string;
  tenant_id: string;
  lead_id: string;
  activity_type: string;
  description: string;
  related_entity_type: string | null;
  related_entity_id: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  created_by: string | null;
  created_by_user?: {
    id: string;
    first_name: string;
    last_name: string;
  } | null;
}

export interface LeadTask {
  id: string;
  tenant_id: string;
  lead_id: string;
  /** Set on a subtask; subtasks are one level deep. */
  parent_task_id?: string | null;
  title: string;
  description: string | null;
  task_type: string;
  due_at: string | null;
  priority: "low" | "medium" | "high" | "urgent";
  status: "pending" | "completed" | "cancelled" | "overdue";
  completed_at: string | null;
  completed_by: string | null;
  assigned_to: string | null;
  assigned_user?: {
    id: string;
    first_name: string;
    last_name: string;
  } | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

export interface LeadAiConversation {
  id: string;
  channel: string;
  status: string;
  started_at: string;
  ended_at: string | null;
  intent_detected: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  contact_name: string | null;
  message_count: number;
  first_user_message: string | null;
}

export interface LeadWithDetails extends Lead {
  activities: LeadActivity[];
  tasks: LeadTask[];
  ai_conversations: LeadAiConversation[];
}

export interface StageConfig {
  key: LeadStage;
  label: string;
  color: string;
  description: string;
}

export interface SourceConfig {
  key: LeadSource;
  label: string;
  icon: string;
}

export interface PipelineStats {
  stageCounts: Record<LeadStage, number>;
  summary: {
    total: number;
    active: number;
    won: number;
    lost: number;
    conversionRate: number;
  };
}

export interface LeadsListResponse {
  success: boolean;
  data: Lead[];
  /**
   * Board mode only (`board: true`): each pipeline column's true total and how
   * many of its leads are in `data`. The board loads a column a page at a
   * time, so `data` is not the whole column.
   */
  columns?: Partial<Record<LeadStage, { total: number; loaded: number }>>;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  message?: string;
}

export interface LeadResponse {
  success: boolean;
  /**
   * Absent when `attached_to_existing` is set — see below. This was declared
   * non-optional, which is why `onCreate(response.data as Lead)` typechecked
   * while handing undefined to the caller.
   */
  data?: Lead | LeadWithDetails;
  message?: string;
  /**
   * POST /leads answers 200 with this when the email already belongs to a
   * member or customer: the enquiry is recorded on that account and NO lead
   * row is created, so there is nothing in `data`.
   */
  attached_to_existing?: boolean;
  existing_account_type?: string;
  membership_id?: string;
}

export interface PipelineStatsResponse {
  success: boolean;
  data: PipelineStats;
  message?: string;
}

export interface StagesConfigResponse {
  success: boolean;
  data: StageConfig[];
  message?: string;
}

export interface SourcesConfigResponse {
  success: boolean;
  data: SourceConfig[];
  message?: string;
}

export interface CreateLeadData {
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  source?: LeadSource;
  source_details?: LeadSourceDetails;
  stage?: LeadStage;
  assigned_to?: string;
  notes?: string;
  interests?: string[];
  preferred_contact_method?: string;
  metadata?: LeadMetadata;
  // Athlete info
  athlete_first_name?: string;
  athlete_last_name?: string;
  athlete_dob?: string;
  athlete_gender?: string;
  // Trial class assignment
  trial_program_id?: string;
  trial_slot_id?: string;
  trial_date?: string;
}

export interface UpdateLeadData {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  source?: LeadSource;
  source_details?: LeadSourceDetails;
  assigned_to?: string | null;
  notes?: string;
  interests?: string[];
  preferred_contact_method?: string;
  metadata?: LeadMetadata;
  // Athlete info
  athlete_first_name?: string;
  athlete_last_name?: string;
  athlete_dob?: string;
  athlete_gender?: string;
  // Trial class assignment
  trial_program_id?: string;
  trial_slot_id?: string;
  trial_date?: string;
}

export interface LeadFilters {
  stage?: string;
  source?: string;
  assigned_to?: string;
  search?: string;
  // ISO date (YYYY-MM-DD) or full ISO timestamp. Backend filters
  // leads.created_at >= this value.
  created_after?: string;
  sort_by?: string;
  sort_order?: "asc" | "desc";
  page?: number;
  limit?: number;
  // Daniel 2026-06-04: archived leads are hidden by default. Pipeline
  // page exposes a toggle that flips this to true.
  include_archived?: boolean;
  // The pipeline board's first load: the first `per_column` leads of EVERY
  // column in one answer, with each column's total in `columns`. More of a
  // column is then loaded with `stage` + `page` + `limit`.
  board?: boolean;
  per_column?: number;
}

// ============================================
// API HELPERS
// ============================================

const getLeadsHeaders = (token: string) => ({
  "Content-Type": "application/json",
  authorization: `Bearer ${token}`,
});

// ============================================
// LEADS CRUD APIs
// ============================================

/**
 * Get all leads with optional filters and pagination
 */
export const getAllLeads = async (
  token: string,
  filters?: LeadFilters
): Promise<LeadsListResponse> => {
  try {
    const params = new URLSearchParams();
    if (filters?.stage) params.append("stage", filters.stage);
    if (filters?.source) params.append("source", filters.source);
    if (filters?.assigned_to) params.append("assigned_to", filters.assigned_to);
    if (filters?.search) params.append("search", filters.search);
    if (filters?.created_after) params.append("created_after", filters.created_after);
    if (filters?.sort_by) params.append("sort_by", filters.sort_by);
    if (filters?.sort_order) params.append("sort_order", filters.sort_order);
    if (filters?.page) params.append("page", filters.page.toString());
    if (filters?.limit) params.append("limit", filters.limit.toString());
    if (filters?.include_archived) params.append("include_archived", "true");
    if (filters?.board) params.append("board", "1");
    if (filters?.per_column) params.append("per_column", filters.per_column.toString());

    const queryString = params.toString();
    const url = `${BASE_URL}/leads${queryString ? `?${queryString}` : ""}`;

    const response = await axios.get(url, {
      headers: getLeadsHeaders(token),
    });
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    return {
      success: false,
      data: [],
      pagination: { page: 1, limit: 50, total: 0, totalPages: 0 },
      message: err.response?.data?.message || err.message || "Failed to fetch leads",
    };
  }
};

/**
 * Get pipeline statistics
 */
export const getPipelineStats = async (
  token: string,
  // The counts leave archived leads out unless the board is showing them:
  // pass the board's own "Show archived" switch.
  options: { includeArchived?: boolean } = {}
): Promise<PipelineStatsResponse> => {
  try {
    const response = await axios.get(`${BASE_URL}/leads/pipeline-stats`, {
      headers: getLeadsHeaders(token),
      ...(options.includeArchived ? { params: { include_archived: "true" } } : {}),
    });
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    return {
      success: false,
      data: {
        stageCounts: {
          new: 0,
          contacted: 0,
          qualified: 0,
          trial_scheduled: 0,
          trial_completed: 0,
          won: 0,
          lost: 0,
        },
        summary: { total: 0, active: 0, won: 0, lost: 0, conversionRate: 0 },
      },
      message: err.response?.data?.message || err.message || "Failed to fetch pipeline stats",
    };
  }
};

/**
 * Get CRM funnel statistics — leads → trials → paid converts with drop-off
 * rates per stage, breakdown by source, and 12-month trend.
 */
export interface FunnelStatsResponse {
  success: boolean;
  data?: {
    totals: {
      all: number;
      won: number;
      lost: number;
      in_progress: number;
      conversion_rate: number;
      win_rate: number;
    };
    by_stage: { stage: string; count: number; percent_of_total: number }[];
    by_source: {
      source: string;
      count: number;
      won: number;
      lost: number;
      conversion_rate: number | null;
    }[];
    monthly: { month: string; created: number; won: number; conversion_rate: number }[];
  };
  message?: string;
}

export const getFunnelStats = async (
  token: string
): Promise<FunnelStatsResponse> => {
  try {
    const response = await axios.get(`${BASE_URL}/leads/funnel-stats`, {
      headers: getLeadsHeaders(token),
    });
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    return {
      success: false,
      message: err.response?.data?.message || err.message || "Failed to fetch funnel stats",
    };
  }
};

/**
 * Get top-N referrers — leaderboard of who's brought us the most leads,
 * with conversion math. Drives the "send Jane a thank-you" surface on
 * the funnel page.
 */
export interface TopReferrer {
  membership_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  referrals: number;
  converted: number;
  lost: number;
  conversion_rate: number | null;
}

export const getTopReferrers = async (
  token: string,
  limit = 10
): Promise<{ success: boolean; data?: TopReferrer[] }> => {
  try {
    const response = await axios.get(`${BASE_URL}/leads/top-referrers?limit=${limit}`, {
      headers: getLeadsHeaders(token),
    });
    return response.data;
  } catch {
    return { success: false };
  }
};

/**
 * Get a single lead by ID with activities and tasks
 *
 * Throws an Error whose `.status` is the HTTP status when available.
 * captureApiError uses that to demote 4xx (expected: stale link,
 * deleted by another session) from error-level to info-level — see
 * utils/sentry.ts:EXPECTED_HTTP_STATUSES. Callers that care about
 * the 404 specifically should branch on `(err as any).status === 404`.
 */
export const getLeadById = async (
  token: string,
  id: string
): Promise<LeadResponse> => {
  try {
    const response = await axios.get(`${BASE_URL}/leads/${id}`, {
      headers: getLeadsHeaders(token),
    });
    return response.data;
  } catch (error: unknown) {
    const err = error as {
      response?: { status?: number; data?: { message?: string } };
      message?: string;
    };
    const wrapped = new Error(
      err.response?.data?.message || err.message || "Failed to fetch lead"
    ) as Error & { status?: number };
    if (typeof err.response?.status === "number") {
      wrapped.status = err.response.status;
    }
    throw wrapped;
  }
};

/**
 * Create a new lead
 */
export const createLead = async (
  token: string,
  data: CreateLeadData
): Promise<LeadResponse> => {
  try {
    const response = await axios.post(`${BASE_URL}/leads`, data, {
      headers: getLeadsHeaders(token),
    });
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string; existingLead?: Lead } }; message?: string };
    const e = new Error(
      err.response?.data?.message || err.message || "Failed to create lead"
    ) as Error & { existingLead?: Lead };
    e.existingLead = err.response?.data?.existingLead;
    throw e;
  }
};

/**
 * Update a lead
 */
export const updateLead = async (
  token: string,
  id: string,
  data: UpdateLeadData
): Promise<LeadResponse> => {
  try {
    const response = await axios.put(`${BASE_URL}/leads/${id}`, data, {
      headers: getLeadsHeaders(token),
    });
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    throw new Error(err.response?.data?.message || err.message || "Failed to update lead");
  }
};

/**
 * Update lead stage
 */
export const updateLeadStage = async (
  token: string,
  id: string,
  stage: LeadStage,
  lost_reason?: string,
  force?: boolean
): Promise<LeadResponse> => {
  try {
    const response = await axios.put(
      `${BASE_URL}/leads/${id}/stage`,
      { stage, lost_reason, force },
      { headers: getLeadsHeaders(token) }
    );
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string; requires_override?: boolean } }; message?: string };
    const enrichedError: any = new Error(err.response?.data?.message || err.message || "Failed to update lead stage");
    enrichedError.requires_override = err.response?.data?.requires_override || false;
    throw enrichedError;
  }
};

/**
 * Assign lead to staff member
 */
export const assignLead = async (
  token: string,
  id: string,
  assigned_to: string | null
): Promise<LeadResponse> => {
  try {
    const response = await axios.put(
      `${BASE_URL}/leads/${id}/assign`,
      { assigned_to },
      { headers: getLeadsHeaders(token) }
    );
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    throw new Error(err.response?.data?.message || err.message || "Failed to assign lead");
  }
};

/**
 * What converting this lead will bill and create. Read-only: the Convert
 * dialog shows it before staff confirm. Rejects when it cannot be read, and
 * the dialog then says so instead of guessing.
 */
export const getConvertPreview = async (
  token: string,
  id: string
): Promise<{ success: boolean; data: LeadConvertPreview }> => {
  const response = await axios.get(`${BASE_URL}/leads/${id}/convert-preview`, {
    headers: getLeadsHeaders(token),
  });
  return response.data;
};

/**
 * Convert lead to membership.
 *
 * If the lead came from an online registration that did not finish, the
 * backend returns 409 with code `WIZARD_FAILED_NEEDS_ACKNOWLEDGEMENT` and,
 * when the card WAS charged, `signup_payment`. The caller surfaces a
 * confirmation dialog saying which, and re-calls with
 * `acknowledge_wizard_failed: true` once staff confirms. A sign-up payment
 * is applied to the account by the conversion itself (billing.signup_payment).
 */
export const convertLead = async (
  token: string,
  id: string,
  // `membership_id` merges this lead INTO that existing membership instead of
  // converting it in place: every record referencing the lead is re-keyed to
  // the target (the backend merge_membership() DB function enumerates FK
  // referrers live), the lead row is snapshotted to the deletion audit log and
  // removed. (The 2026-08-08 removal was of a FAKE merge that re-keyed only 3
  // columns; this is the real one, added 2026-08-18.)
  //
  // `bill_now: false` converts without billing anything: no registration fee,
  // no enrollments and no class invoices. Left out, converting bills as usual.
  options: { acknowledge_wizard_failed?: boolean; membership_id?: string; bill_now?: boolean } = {}
): Promise<{
  success: boolean;
  data?: Lead;
  membership_id?: string;
  merged?: boolean;
  merge_summary?: Record<string, unknown>;
  enrollments?: any[];
  billing?: LeadConvertBilling;
  message?: string;
  code?: string;
  wizard_failed?: { reason?: string | null; at?: string | null; original_message?: string | null };
  signup_payment?: LeadSignupPayment | null;
}> => {
  try {
    const response = await axios.post(
      `${BASE_URL}/leads/${id}/convert`,
      options,
      { headers: getLeadsHeaders(token) }
    );
    return response.data;
  } catch (error: unknown) {
    const err = error as {
      response?: {
        status?: number;
        data?: {
          message?: string;
          code?: string;
          wizard_failed?: { reason?: string; at?: string; original_message?: string };
          signup_payment?: LeadSignupPayment | null;
        };
      };
      message?: string;
    };
    // 409 with WIZARD_FAILED_NEEDS_ACKNOWLEDGEMENT is a structured
    // signal — return it so the UI can show a confirm dialog rather
    // than a generic "Failed to convert lead" toast.
    if (
      err.response?.status === 409 &&
      err.response?.data?.code === "WIZARD_FAILED_NEEDS_ACKNOWLEDGEMENT"
    ) {
      return {
        success: false,
        code: err.response.data.code,
        message: err.response.data.message,
        wizard_failed: err.response.data.wizard_failed,
        signup_payment: err.response.data.signup_payment ?? null,
      };
    }
    throw new Error(err.response?.data?.message || err.message || "Failed to convert lead");
  }
};

export interface LeadDuplicateCandidate {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
  account_type?: string | null;
  status?: string | null;
  balance?: number | string | null;
  created_at?: string | null;
  match_reasons?: string[];
}

/**
 * Likely existing memberships for this lead (same phone digits, same email
 * on any email column, or same name). Backs the convert flow's
 * "merge into existing member instead?" prompt.
 */
export const getLeadDuplicateCandidates = async (
  token: string,
  id: string
): Promise<{ success: boolean; data: LeadDuplicateCandidate[] }> => {
  try {
    const response = await axios.get(`${BASE_URL}/leads/${id}/duplicate-candidates`, {
      headers: getLeadsHeaders(token),
    });
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    throw new Error(
      err.response?.data?.message || err.message || "Failed to check for duplicates"
    );
  }
};

/**
 * Archive a lead (Daniel 2026-06-04: no more hard deletes).
 *
 * The DELETE /:id route still exists for backward compatibility but
 * the backend now performs a soft-archive (sets archived_at). Archived
 * leads are hidden from the pipeline + broadcast audiences by default;
 * see `restoreLead` and the `include_archived` filter to surface them.
 */
export const archiveLead = async (
  token: string,
  id: string
): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await axios.delete(`${BASE_URL}/leads/${id}`, {
      headers: getLeadsHeaders(token),
    });
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    throw new Error(err.response?.data?.message || err.message || "Failed to archive lead");
  }
};

/**
 * @deprecated Use archiveLead — leads can no longer be hard-deleted.
 */
export const deleteLead = archiveLead;

/**
 * Restore a previously-archived lead.
 */
export const restoreLead = async (
  token: string,
  id: string
): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await axios.post(
      `${BASE_URL}/leads/${id}/restore`,
      null,
      { headers: getLeadsHeaders(token) }
    );
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    throw new Error(err.response?.data?.message || err.message || "Failed to restore lead");
  }
};

/**
 * Bulk update leads (for kanban drag-drop)
 */
export const bulkUpdateLeads = async (
  token: string,
  updates: Array<{ id: string; stage?: LeadStage; assigned_to?: string | null }>
): Promise<{ success: boolean; data: Lead[]; errors?: Array<{ id: string; error: string }> }> => {
  try {
    const response = await axios.put(
      `${BASE_URL}/leads/bulk-update`,
      { updates },
      { headers: getLeadsHeaders(token) }
    );
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    throw new Error(err.response?.data?.message || err.message || "Failed to bulk update leads");
  }
};

/**
 * Archive several leads in one step. Nothing is deleted: archived leads stay
 * reachable under Manage Memberships (status filter) and in Broadcasts.
 * `archived_count` is what the server actually archived.
 */
export const bulkArchiveLeads = async (
  token: string,
  ids: string[]
): Promise<{
  success: boolean;
  archived_count: number;
  archived_ids: string[];
  skipped_count: number;
  message?: string;
}> => {
  try {
    const response = await axios.post(
      `${BASE_URL}/leads/bulk-archive`,
      { ids },
      { headers: getLeadsHeaders(token) }
    );
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    throw new Error(err.response?.data?.message || err.message || "Failed to archive leads");
  }
};

/**
 * Archive EVERY lead in one pipeline column, including the ones the board has
 * not loaded. The server works the column out itself from the same filters
 * the board is using; `expectedCount` is the number the person was shown, and
 * the server archives nothing if the column has grown past it.
 */
export const bulkArchiveLeadColumn = async (
  token: string,
  column: { stage: LeadStage; source?: string; assigned_to?: string; search?: string },
  expectedCount: number
): Promise<{
  success: boolean;
  archived_count: number;
  archived_ids: string[];
  skipped_count: number;
  message?: string;
}> => {
  try {
    const response = await axios.post(
      `${BASE_URL}/leads/bulk-archive`,
      { column, expected_count: expectedCount },
      { headers: getLeadsHeaders(token) }
    );
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    throw new Error(err.response?.data?.message || err.message || "Failed to archive leads");
  }
};

export interface ConvertedLeadReviewInvoice {
  id: string;
  amount: number;
  description: string | null;
  due_date: string | null;
  created_at: string | null;
  status: string;
}

export interface ConvertedLeadReviewRow {
  membership_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  status: string | null;
  account_type: string | null;
  lead_source: string | null;
  converted_at: string | null;
  amount_owed: number;
  unpaid_invoices: ConvertedLeadReviewInvoice[];
  unpaid_invoice_count: number;
  oldest_unpaid_due_date: string | null;
}

/**
 * Read-only review list: members who came from a lead, have nobody in a
 * class, and owe money. Throws on failure (with `status` set), so the page
 * can tell "nobody to review" from "could not load".
 */
export const getConvertedLeadsWithoutClass = async (
  token: string
): Promise<{
  success: boolean;
  data: ConvertedLeadReviewRow[];
  summary: { families: number; total_owed: number };
}> => {
  try {
    const response = await axios.get(`${BASE_URL}/leads/converted-without-class`, {
      headers: getLeadsHeaders(token),
    });
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { status?: number; data?: { message?: string } }; message?: string };
    const thrown = new Error(
      err.response?.data?.message || err.message || "Could not load the list"
    ) as Error & { status?: number };
    thrown.status = err.response?.status;
    throw thrown;
  }
};

// ============================================
// LEAD ACTIVITIES APIs
// ============================================

/**
 * Get activities for a lead
 */
export const getLeadActivities = async (
  token: string,
  leadId: string,
  options?: { limit?: number; offset?: number }
): Promise<{ success: boolean; data: LeadActivity[]; total: number }> => {
  try {
    const params = new URLSearchParams();
    if (options?.limit) params.append("limit", options.limit.toString());
    if (options?.offset) params.append("offset", options.offset.toString());

    const queryString = params.toString();
    const url = `${BASE_URL}/leads/${leadId}/activities${queryString ? `?${queryString}` : ""}`;

    const response = await axios.get(url, {
      headers: getLeadsHeaders(token),
    });
    return response.data;
  } catch {
    return { success: false, data: [], total: 0 };
  }
};

/**
 * Add activity to a lead
 */
export const addLeadActivity = async (
  token: string,
  leadId: string,
  data: { activity_type: string; description: string; metadata?: Record<string, unknown> }
): Promise<{
  success: boolean;
  data: LeadActivity;
  message?: string;
  lead_contact?: LeadContactResult | null;
  lead_reply?: LeadReplyResult | null;
}> => {
  try {
    const response = await axios.post(
      `${BASE_URL}/leads/${leadId}/activities`,
      data,
      { headers: getLeadsHeaders(token) }
    );
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    throw new Error(err.response?.data?.message || err.message || "Failed to add activity");
  }
};

/**
 * Send email to a lead
 */
export const sendLeadEmail = async (
  token: string,
  leadId: string,
  data: { to: string; subject: string; message: string; user?: { first_name?: string; last_name?: string }; clubName?: string }
): Promise<{ success: boolean; data: LeadActivity; message?: string; lead_contact?: LeadContactResult | null }> => {
  try {
    const response = await axios.post(
      `${BASE_URL}/leads/${leadId}/email`,
      data,
      { headers: getLeadsHeaders(token) }
    );
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    throw new Error(err.response?.data?.message || err.message || "Failed to send email");
  }
};

// ============================================
// LEAD TASKS APIs
// ============================================

/**
 * Get tasks for a lead
 */
export const getLeadTasks = async (
  token: string,
  leadId: string,
  status?: string
): Promise<{ success: boolean; data: LeadTask[] }> => {
  try {
    const params = new URLSearchParams();
    if (status) params.append("status", status);

    const queryString = params.toString();
    const url = `${BASE_URL}/leads/${leadId}/tasks${queryString ? `?${queryString}` : ""}`;

    const response = await axios.get(url, {
      headers: getLeadsHeaders(token),
    });
    return response.data;
  } catch {
    return { success: false, data: [] };
  }
};

/**
 * Create a task for a lead
 */
export const createLeadTask = async (
  token: string,
  leadId: string,
  data: {
    title: string;
    description?: string;
    task_type?: string;
    due_at?: string;
    priority?: string;
    assigned_to?: string;
  }
): Promise<{ success: boolean; data: LeadTask; message?: string }> => {
  try {
    const response = await axios.post(
      `${BASE_URL}/leads/${leadId}/tasks`,
      data,
      { headers: getLeadsHeaders(token) }
    );
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    throw new Error(err.response?.data?.message || err.message || "Failed to create task");
  }
};

/**
 * Update a lead task
 */
export const updateLeadTask = async (
  token: string,
  leadId: string,
  taskId: string,
  data: {
    title?: string;
    /** null clears it; undefined leaves it unchanged. */
    description?: string | null;
    task_type?: string;
    due_at?: string;
    priority?: string;
    /** null unassigns; undefined leaves it unchanged. */
    assigned_to?: string | null;
    status?: string;
  }
): Promise<{ success: boolean; data: LeadTask; message?: string }> => {
  try {
    const response = await axios.put(
      `${BASE_URL}/leads/${leadId}/tasks/${taskId}`,
      data,
      { headers: getLeadsHeaders(token) }
    );
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    throw new Error(err.response?.data?.message || err.message || "Failed to update task");
  }
};

/**
 * Delete a lead task
 */
export const deleteLeadTask = async (
  token: string,
  leadId: string,
  taskId: string
): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await axios.delete(
      `${BASE_URL}/leads/${leadId}/tasks/${taskId}`,
      { headers: getLeadsHeaders(token) }
    );
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    throw new Error(err.response?.data?.message || err.message || "Failed to delete task");
  }
};

// ============================================
// CONFIG APIs
// ============================================

/**
 * Get stages configuration
 */
export const getStagesConfig = async (
  token: string
): Promise<StagesConfigResponse> => {
  try {
    const response = await axios.get(`${BASE_URL}/leads/config/stages`, {
      headers: getLeadsHeaders(token),
    });
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    return {
      success: false,
      data: [],
      message: err.response?.data?.message || err.message || "Failed to fetch stages config",
    };
  }
};

/**
 * Get sources configuration
 */
export const getSourcesConfig = async (
  token: string
): Promise<SourcesConfigResponse> => {
  try {
    const response = await axios.get(`${BASE_URL}/leads/config/sources`, {
      headers: getLeadsHeaders(token),
    });
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    return {
      success: false,
      data: [],
      message: err.response?.data?.message || err.message || "Failed to fetch sources config",
    };
  }
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * What to call a lead wherever it is shown: its name, else its email, else
 * its phone number, else "Unnamed lead". Never "null null" — leads captured
 * by the AI chat often have no name at all (utils/personName owns the rule).
 */
export const getLeadFullName = (lead: {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
}): string => leadDisplayName(lead);

/**
 * Get stage label for display
 */
export const getStageLabel = (stage: LeadStage): string => {
  const labels: Record<LeadStage, string> = {
    new: "New",
    contacted: "Contacted",
    qualified: "Qualified",
    trial_scheduled: "Trial Scheduled",
    trial_completed: "Trial Completed",
    won: "Won",
    lost: "Lost",
  };
  return labels[stage] || stage;
};

export const getStageDescription = (stage: LeadStage): string => {
  const descriptions: Record<LeadStage, string> = {
    new: "Newly captured lead — no contact yet",
    contacted: "Initial contact made — awaiting response",
    qualified: "All member and athlete info collected — ready to enroll",
    trial_scheduled: "Optional trial class booked",
    trial_completed: "Trial class attended — follow up for enrollment",
    won: "Fully converted — membership, enrollment, and payment complete",
    lost: "Did not convert",
  };
  return descriptions[stage] || "";
};

/**
 * Get source label for display
 */
export const getSourceLabel = (source: LeadSource): string => {
  const labels: Record<LeadSource, string> = {
    facebook_ads: "Facebook Ads",
    google_ads: "Google Ads",
    linkedin: "LinkedIn",
    website_form: "Website Form",
    phone_call: "Phone Call",
    email: "Email",
    referral: "Referral",
    walk_in: "Walk-In",
    instagram: "Instagram",
    tiktok: "TikTok",
    other: "Other",
    registration_form: "Registration Form",
    contact_form: "Contact Form",
    ai_receptionist: "AI Receptionist",
    ai_chat: "AI Chat",
    trial_signup: "Trial Signup",
  };
  return labels[source] || source;
};

/**
 * A lead activity's text for the screen. The server records how a lead came
 * in as "Lead created from walk_in" (the stored source code); shown as is,
 * staff read the code (2026-10-10). A known source code at the end of the
 * text becomes its label: "Lead created from Walk-In". Anything else is
 * returned unchanged, so older and newer wordings both read correctly.
 */
export const leadActivityText = (description: string | null | undefined): string => {
  const text = String(description ?? "");
  const match = /^(.*\bfrom )([a-z][a-z0-9_]*)$/.exec(text);
  if (!match) return text;
  const label = getSourceLabel(match[2] as LeadSource);
  return label === match[2] ? text : `${match[1]}${label}`;
};

/**
 * Get stage color for styling
 */
export const getStageColor = (stage: LeadStage): string => {
  const colors: Record<LeadStage, string> = {
    new: "#3B82F6",
    contacted: "#8B5CF6",
    qualified: "#F59E0B",
    trial_scheduled: "#10B981",
    trial_completed: "#14B8A6",
    won: "#22C55E",
    lost: "#EF4444",
  };
  return colors[stage] || "#6B7280";
};
